import { useRef, useState } from "react";
import { FolderOpen, Upload, X } from "lucide-react";
import { toast } from "sonner";
import MediaPickerModal from "@/components/MediaPickerModal";
import { resolveMediaUrl, uploadMediaFile } from "@/lib/media-api";

export default function WorkspaceMediaField({
  label,
  value,
  onChange,
  kind = "image",
  hint = "",
  testId = "",
}) {
  const inputRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const preview = resolveMediaUrl(value);
  const isImage = kind !== "document";

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await uploadMediaFile(file);
      const url = data?.asset?.url;
      if (!url) throw new Error("Upload response missing url");
      onChange(url);
      toast.success("Uploaded to workspace library");
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="block md:col-span-2" data-testid={testId || undefined}>
      <span className="text-overline">{label}</span>
      {hint ? <p className="text-xs text-[var(--text-tertiary)] mt-1">{hint}</p> : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {preview && isImage ? (
          <div className="flex items-center gap-2 surface px-3 py-2 border border-[var(--border-default)]">
            <img src={preview} alt="" className="h-12 w-auto max-w-[140px] object-contain" />
            <button
              type="button"
              className="btn-ghost p-1.5 text-[var(--text-tertiary)] hover:text-[var(--error)]"
              onClick={() => onChange("")}
              aria-label="Clear"
            >
              <X size={14} />
            </button>
          </div>
        ) : null}
        {preview && !isImage ? (
          <span className="text-xs font-mono text-[var(--text-secondary)] truncate max-w-[240px]">{value}</span>
        ) : null}
        <button type="button" className="btn-secondary text-xs" onClick={() => setPickerOpen(true)}>
          <FolderOpen size={12} /> Library
        </button>
        <button
          type="button"
          className="btn-secondary text-xs"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={kind === "document" ? ".pdf,application/pdf" : "image/*"}
          onChange={(ev) => {
            const file = ev.target.files?.[0];
            uploadFile(file);
          }}
        />
      </div>
      <input
        className="input-dark mt-2 text-xs font-mono"
        value={value || ""}
        onChange={(ev) => onChange(ev.target.value)}
        placeholder="https://… or pick from library"
      />
      <MediaPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        kind={kind}
        title={label}
        onSelect={(asset) => onChange(asset?.url || "")}
      />
    </div>
  );
}
